import _ from 'lodash';
import moment from 'moment';

import { SessaoUsuario } from '~/helpers/token';

import logoPadrao from '../assets/images/logo_contrato_padrao.png';
import { encrypt, decrypt, eaPassword } from './crypto';

// Remover não-numéricos
export function stripNonNumeric(value) {
  return value.replace(/[\D]+/g, '');
}

// Normaliza CPF/CNPJ, preservando os caracteres alfanumericos do novo CNPJ
export function normalizeDocument(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14);
}

// Mascara para documento
export function maskDocument(value) {
  const newValue = normalizeDocument(value);
  if (newValue) {
    if (/^\d{11}$/.test(newValue)) {
      return newValue.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (/^[A-Z0-9]{12}\d{2}$/.test(newValue)) {
      return newValue.replace(
        /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
      );
    }
  }
  return value;
}

export function CustomReplace(str, oldStr, newStr) {
  return str.split(oldStr).join(newStr);
}

// Verificar se string contém só números
export function isOnlyNumeric(value) {
  return /^\d+$/.test(value);
}

export function formatarPorcentagem(value) {
  return `${CustomReplace(value.toString(), '.', ',')} %`;
}

export function formatarData(date, dateFormat = 'L') {
  if (date) {
    let newDate = date;

    if (typeof date === 'string') {
      if (date.toLowerCase() === 'invalid date') {
        return null;
      }

      if (date.split(' ').length === 3) {
        newDate = moment(date, 'DD MMM YYYY').format(dateFormat);
      }
    }

    return moment(newDate, dateFormat);
  }
  return null;
}

export function dataPtBr(date, dateFormat = 'L') {
  if (date) {
    let newDate = date;
    if (dateFormat === 'LL') {
      newDate = moment(date, dateFormat);
    }
    return moment(newDate).format(dateFormat);
  }
  return date;
}

export const stringToFloat = (valor) => {
  if (typeof valor === 'string') {
    return parseFloat(valor.replace(',', '.'));
  }
  return valor;
};

// Converter número para real
export function formatarReal(valor, prefixo) {
  if (valor !== null && valor !== undefined && valor !== '') {
    if (prefixo) {
      const novoValor = stringToFloat(valor);
      // ERPC-10
      if (Number.isNaN(novoValor)) {
        return valor;
      }

      return JSON.parse(novoValor).toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL',
      });
    }
    // ERPC-10
    if (Number.isNaN(valor)) {
      return valor;
    }

    return JSON.parse(valor).toLocaleString('pt-br', {
      minimumFractionDigits: 2,
    });
  }
  return valor;
}

// Cria query string
export function getQueryString(filtros) {
  let i = 1;

  const stringFiltros = _.map(filtros, (value, key) => {
    if (value || value === false) {
      if (i === 1) {
        i += 1;
        return `?${key}=${value}`;
      }
      i += 1;
      return `&${key}=${value}`;
    }
    return '';
  });
  return stringFiltros.join('');
}

// Formata a string do módulo
export function formatModulo(modulo) {
  switch (modulo) {
    case 'eRecuperadorPisCofins':
      return 'Recuperação de PIS/COFINS para empresas do Simples Nacional';
    case 'eRecuperadorSegregadorIcms':
      return 'Segregação de Receitas para empresas do Simples Nacional - PIS/COFINS e ICMS';
    case 'ExclusaoIcms':
      return 'Exclusão do ICMS da base de cálculo do PIS/COFINS';
    case 'ExclusaoIssqn':
      return 'Exclusão do ISSQN da base de cálculo do PIS/COFINS';
    case 'VerbasInss':
      return 'Verbas Indenizatórias';
    case 'ContribuicoesTerceiros':
      return 'Contribuições para Terceiros';
    case 'RecuperacaoCreditoTributario':
      return 'Diagnóstico RCT';
    case 'PISCOFINSSTSobreCigarros':
      return 'PIS/COFINS ST Sobre Cigarros';
    case 'INSSPessoaFisica':
      return 'Recuperação Previdenciária acima do teto de contribuição';
    case 'SubvencoesICMS':
      return 'Cálculo do benefício fiscal de ICMS.';
    case 'RevisorPISCOFINS':
      return 'Revisão tributária de PIS/COFINS';
    default:
      return modulo;
  }
}

// Preenche o template com os campos
export const preencherTemplate = async (campos, template) => {
  let novoTemplate = template;

  Promise.all(
    campos.map((field) => {
      const { nome, valor, tipo } = field;
      const regExp = new RegExp(`@${nome}@`, 'g');
      let novoValor = valor;

      switch (tipo) {
        case 'percentual':
          novoValor = novoValor.replace(/[^a-z0-9,/]/g, '');
          break;
        case 'moeda':
          novoValor = formatarReal(
            parseFloat(novoValor.replace(/[^a-z0-9,/]/g, '').replace(',', '.')),
            true
          );
          break;
        case 'texto':
          novoValor = novoValor.replace(/\n/g, '<br />');
          break;
        case 'cpfCnpj':
          novoValor = maskDocument(novoValor);
          break;
        default:
          break;
      }

      novoTemplate = novoTemplate.replace(regExp, novoValor);
      return true;
    })
  );

  return novoTemplate;
};

function normalizeImageSrc(value) {
  if (!value || typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^data:image\//i.test(trimmed)) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  // If the backend returns only the base64 payload, make it a data URL (defaulting to png).
  if (/^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 50) {
    return `data:image/png;base64,${trimmed}`;
  }

  // Make relative URLs absolute for contexts like TinyMCE print windows.
  if (typeof window !== 'undefined' && window.location?.origin) {
    if (trimmed.startsWith('/')) return `${window.location.origin}${trimmed}`;
    return `${window.location.origin}/${trimmed}`;
  }

  return trimmed;
}

const logoPadraoNormalized = normalizeImageSrc(logoPadrao);

export const setPageHeader = async (template) => {
  const rawLogo = SessaoUsuario('fotoPerfil');
  const logo = normalizeImageSrc(rawLogo);

  if (logo && logoPadraoNormalized && logo !== logoPadraoNormalized) {
    const prevContent =
      "<table style='border: 0'><thead><tr><td style='border: 0'>" +
      "<div style='height: 2cm'>&nbsp;</div>" +
      "</td></tr></thead><tbody><tr style='border: 0'><td style='border: 0'><div>";
    const afterContent =
      "</div></td></tr></tbody><tfoot><tr><td style='border: 0'>" +
      "<div style='height: 1cm'>&nbsp;</div>" +
      '</td></tr></tfoot></table>';
    const header =
      `<div style='position: fixed; top: 0; display: flex; justify-content: center; align-items: center; width: 100%; height: 2cm;'>` +
      `<img src='${logo}' alt='' style='height: 2cm;' /></div>`;
    const footer = `<div style='position: fixed; bottom: 0; display: flex; justify-content: center; align-items: center; width: 100%; height: 1cm;'></div>`;

    return prevContent + template + afterContent + header + footer;
  }
  return template;
};

// Criptografa um objeto
export function encryptObject(data) {
  return encodeURIComponent(encrypt(encodeURIComponent(JSON.stringify(data)), eaPassword));
}

// Decriptografa um objeto
export function decryptObject(dataStr) {
  return JSON.parse(decodeURIComponent(decrypt(decodeURIComponent(dataStr), eaPassword)));
}
